import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { isNetworkError, queueInspection } from "../lib/offlineStore";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { PhotoUploader } from "../components/PhotoUploader";
import { useAuth } from "../context/AuthContext";
import { InspectionResult } from "../components/InspectionResult";
import { APP } from "../constants/testIds";

const TYPE_MAP = {
  "avant-poste": { key: "QUOTIDIENNE_AVANT_POSTE", label: "Visite quotidienne du front avant prise de poste" },
  "apres-tir": { key: "APRES_TIR", label: "Visite du front après tir de mine" },
};

export default function VisitForm() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const presetFrontId = searchParams.get("front") || "";
  const navigate = useNavigate();
  const { selectedSite } = useAuth();
  const meta = TYPE_MAP[type];

  const [fronts, setFronts] = useState([]);
  const [frontId, setFrontId] = useState(presetFrontId);
  const [presetFrontLabel, setPresetFrontLabel] = useState("");
  const [useAutre, setUseAutre] = useState(false);
  const [frontAutre, setFrontAutre] = useState("");
  const [numeroTir, setNumeroTir] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [purge, setPurge] = useState(false);
  const [commentaire, setCommentaire] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    const calls = [api.get(`/questions?type_inspection=${meta.key}`).then((r) => r.data)];
    if (presetFrontId) {
      calls.push(api.get(`/fronts/${presetFrontId}`).then((r) => r.data));
    } else {
      calls.push(api.get(`/fronts?sort=geo&site=${selectedSite}`).then((r) => r.data));
    }
    Promise.all(calls).then(([q, second]) => {
      setQuestions(q);
      if (presetFrontId) {
        setPresetFrontLabel(second.front?.Front_Libelle || presetFrontId);
      } else {
        setFronts(second);
      }
    }).catch((err) => {
      setError(err.response?.status === 403 ? "Accès refusé — ce front appartient à un autre site." : "Impossible de charger cette visite.");
    }).finally(() => setLoading(false));
  }, [meta, presetFrontId, selectedSite]);

  const setAnswer = (qid, field, value) => setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], [field]: value } }));
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.Question_ID]?.reponse);
  const frontValid = presetFrontId ? true : (useAutre ? frontAutre.trim().length > 0 : frontId.length > 0);
  const missingCriticalPhoto = questions.some((q) => {
    const ans = answers[q.Question_ID] || {};
    return ans.reponse === q.Reponse_Dangereuse && q.Criticite_Anomalie === "CRITIQUE" && !(ans.photos && ans.photos.length);
  });
  const canSubmit = allAnswered && frontValid && numeroTir.trim().length > 0 && !missingCriticalPhoto;

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);
    const reponses = questions.map((q) => ({
      question_id: q.Question_ID,
      reponse: answers[q.Question_ID]?.reponse,
      commentaire: answers[q.Question_ID]?.commentaire || "",
      photo_url: (answers[q.Question_ID]?.photos || [])[0] || "",
    }));
    const payload = {
      front_id: presetFrontId || (useAutre ? "" : frontId),
      front_autre: !presetFrontId && useAutre ? frontAutre : "",
      type_inspection: meta.key, numero_tir: numeroTir, reponses, purge_effectuee: purge, commentaire,
    };
    try {
      const { data } = await api.post("/inspections", payload);
      setResult(data);
    } catch (err) {
      if (isNetworkError(err)) {
        queueInspection(payload);
        setResult({ offline: true });
      } else {
        setError(apiErrorMessage(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) {
    return <div className="min-h-[60vh] flex items-center justify-center text-red-400 font-mono uppercase text-sm">Type de visite inconnu.</div>;
  }
  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement...</div>;
  }
  if (error) {
    return <div data-testid="visit-form-error" className="min-h-[60vh] flex items-center justify-center text-red-400 font-mono uppercase text-sm text-center px-4">{error}</div>;
  }
  if (result) {
    return <InspectionResult result={result} backTo={presetFrontId ? `/front/${presetFrontId}` : "/"} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="visit-form-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-500 mb-1">Nouvelle visite terrain</div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-50">{meta.label}</h1>
        {presetFrontId && <p className="text-sm text-slate-400 mt-1">Front : {presetFrontLabel}</p>}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-4">
        {!presetFrontId && (
          <div className="space-y-2">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Front concerné</label>
            {!useAutre ? (
              <select data-testid="visit-front-select" value={frontId} onChange={(e) => setFrontId(e.target.value)} className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100">
                <option value="">— Choisir un front —</option>
                {fronts.map((f) => <option key={f.Front_ID} value={f.Front_ID}>{f.Front_Libelle}</option>)}
              </select>
            ) : (
              <Input data-testid="visit-front-autre-input" placeholder="Nom du front (non listé)" value={frontAutre} onChange={(e) => setFrontAutre(e.target.value)} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
            )}
            <button type="button" data-testid="visit-front-toggle-autre" onClick={() => setUseAutre(!useAutre)} className="text-xs text-cyan-400 hover:underline">
              {useAutre ? "← Choisir dans la liste" : "Front non listé ? Saisie libre →"}
            </button>
          </div>
        )}
        <div className="space-y-2">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">N° du tir</label>
          <Input data-testid="visit-numero-tir-input" placeholder="Ex: T-2026-045" value={numeroTir} onChange={(e) => setNumeroTir(e.target.value)} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
        </div>
      </div>

      {questions.map((q) => {
        const ans = answers[q.Question_ID] || {};
        const isDangerAnswer = ans.reponse && ans.reponse === q.Reponse_Dangereuse;
        return (
          <div key={q.Question_ID} className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-2">
            <p className="text-sm text-slate-200">
              {q.Question} {q.Criticite_Anomalie === "CRITIQUE" && <span className="text-red-400 text-xs font-bold ml-1">[CRITIQUE]</span>}
            </p>
            {q.Aide_Terrain && <p className="text-xs text-slate-500 italic">{q.Aide_Terrain}</p>}
            <div className="flex gap-2 flex-wrap">
              {(q.options_list || []).map((opt) => {
                const isActive = ans.reponse === opt;
                const isConforme = opt === q.Reponse_Conforme;
                const activeClass = isConforme ? "bg-emerald-500 text-black border-emerald-500" : "bg-red-600 text-white border-red-600";
                return (
                  <button
                    key={opt}
                    type="button"
                    data-testid={APP.checklistItemToggle(q.Question_ID, opt)}
                    onClick={() => setAnswer(q.Question_ID, "reponse", opt)}
                    className={`rounded-lg border-2 px-5 py-2.5 text-xs font-bold uppercase min-h-[48px] transition-colors ${
                      isActive ? activeClass : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {isDangerAnswer && q.Commentaire_si_Anomalie === "TRUE" && (
              <Textarea
                data-testid={`visit-comment-${q.Question_ID}`}
                placeholder="Commentaire (optionnel)"
                value={ans.commentaire || ""}
                onChange={(e) => setAnswer(q.Question_ID, "commentaire", e.target.value)}
                className="bg-[#0A0D12] border-slate-700 text-slate-100 text-sm"
              />
            )}
            {isDangerAnswer && q.Criticite_Anomalie === "CRITIQUE" && (
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-widest text-red-400">Photo obligatoire (preuve du danger)</label>
                <PhotoUploader photos={ans.photos || []} onChange={(p) => setAnswer(q.Question_ID, "photos", p)} testId={`visit-photos-${q.Question_ID}`} />
              </div>
            )}
          </div>
        );
      })}

      <div className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-3">
        <label className="flex items-center gap-2.5 text-sm text-slate-200 cursor-pointer">
          <Checkbox data-testid="visit-purge-checkbox" checked={purge} onCheckedChange={setPurge} />
          Purge réalisée lors de cette visite
        </label>
        <Textarea
          data-testid="visit-general-comment"
          placeholder="Commentaire général (optionnel)"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          className="bg-[#0A0D12] border-slate-700 text-slate-100 text-sm"
        />
      </div>

      {error && <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">{error}</div>}
      {missingCriticalPhoto && (
        <div data-testid="visit-missing-critical-photo-warning" className="text-xs text-amber-400 bg-amber-950/30 border border-amber-900 rounded-lg px-3 py-2">
          Une photo est obligatoire pour chaque réponse critique signalée comme dangereuse.
        </div>
      )}

      <Button
        data-testid="visit-submit-button"
        disabled={!canSubmit || submitting}
        onClick={handleSubmit}
        className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black font-bold uppercase tracking-wide"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider la visite"}
      </Button>
    </div>
  );
}
